import {
    DocumentProcessingStatus,
    getDocumentProcessingStatus,
    shouldPollDocument,
} from './document-processing'
import {
    DocumentResearchStatus,
    isActionRequiredDocumentResearchStatus,
    isActiveDocumentResearchStatus,
    isTerminalDocumentResearchStatus,
} from './document-research'

const DEFAULT_POLL_INTERVAL = 5000
const DEFAULT_REVIEW_REFRESH_DELAY = 2000
const DEFAULT_REVIEW_REFRESH_INTERVAL = 3000
const DEFAULT_REVIEW_REFRESH_MAX_ATTEMPTS = 5
const DEFAULT_RESEARCH_POLL_INTERVAL = 5000
const RESEARCH_STATUS_BATCH_SIZE = 50

const getService = (context, name) => {
    const service = context.services && context.services[name]

    if (typeof service !== 'function') {
        throw new Error(`Documents and Webpages requires services.${name}()`)
    }

    return service
}

const hasRequestedVerificationDocuments = context => (
    context.request
    && Array.isArray(context.request.verificationDocuments)
    && context.request.verificationDocuments.length > 0
)

const createDocumentsAndWebpagesController = ({
    context,
    step,
    setSaving,
    reportError,
    publishEffect,
    pollInterval = DEFAULT_POLL_INTERVAL,
    reviewRefreshDelay = DEFAULT_REVIEW_REFRESH_DELAY,
    reviewRefreshInterval = DEFAULT_REVIEW_REFRESH_INTERVAL,
    reviewRefreshMaxAttempts = DEFAULT_REVIEW_REFRESH_MAX_ATTEMPTS,
    researchPollInterval = DEFAULT_RESEARCH_POLL_INTERVAL,
} = {}) => {
    const linksEnabled = !step || !step.settings || step.settings.showWebpageLinks !== false
    let validation = { isValid: true, errors: [] }
    let uploadPromise = null
    let linkSavePromise = null
    let refreshPromise = null
    let verificationRefreshPromise = null
    let refreshLinksPromise = null
    let refreshResearchPromise = null
    let researchCommandPromise = null
    let pollTimeout = null
    let researchPollTimeout = null
    let reviewRefreshTimeout = null
    const reviewRefreshAttempts = new Map()
    let active = false
    let disposed = false
    const forcedResearchPollProcessIds = new Set()

    const stopPolling = () => {
        clearTimeout(pollTimeout)
        pollTimeout = null
    }

    const shouldPoll = () => context.documentsAndWebpages.documents.some(shouldPollDocument)

    const schedulePolling = () => {
        stopPolling()

        if (!active || disposed || !pollInterval || !shouldPoll()) {
            return
        }

        pollTimeout = setTimeout(() => {
            pollTimeout = null
            refreshDocuments()
                .catch(() => undefined)
                .finally(schedulePolling)
        }, pollInterval)
    }

    const stopResearchPolling = () => {
        clearTimeout(researchPollTimeout)
        researchPollTimeout = null
    }

    const researchProcessIds = () => Array.from(new Set(
        context.documentsAndWebpages.links
            .map(link => link && link.documentResearch && link.documentResearch.processId)
            .filter(processId => processId !== null && typeof processId !== 'undefined' && processId !== '')
            .map(String),
    ))

    const shouldPollResearch = () => context.documentsAndWebpages.researchProcesses.some(process => (
        isActiveDocumentResearchStatus(process.status)
        || forcedResearchPollProcessIds.has(String(process.processId))
    ))

    const scheduleResearchPolling = () => {
        stopResearchPolling()

        if (!active || disposed || !researchPollInterval || !shouldPollResearch()) {
            return
        }

        researchPollTimeout = setTimeout(() => {
            researchPollTimeout = null
            refreshResearch()
                .catch(() => undefined)
                .finally(scheduleResearchPolling)
        }, researchPollInterval)
    }

    const documentKey = document => {
        const processId = document && document.processing && document.processing.processId

        return processId || (document && document.id) || null
    }

    const completedDocumentKeys = documents => new Set((documents || [])
        .filter(document => getDocumentProcessingStatus(document) === DocumentProcessingStatus.COMPLETED)
        .map(documentKey)
        .filter(key => key !== null)
        .map(String))

    const applyDocuments = async response => {
        const documents = response && Array.isArray(response.data) ? response.data : []
        const previousDocuments = context.documentsAndWebpages.documents
        const hadPreviousSnapshot = context.documentsAndWebpages.loaded === true
        const previouslyCompleted = completedDocumentKeys(previousDocuments)
        const hasNewlyCompletedDocument = hadPreviousSnapshot && documents.some(document => {
            const key = documentKey(document)

            return key !== null
                && getDocumentProcessingStatus(document) === DocumentProcessingStatus.COMPLETED
                && !previouslyCompleted.has(String(key))
        })

        context.documentsAndWebpages.documents.splice(
            0,
            context.documentsAndWebpages.documents.length,
            ...documents,
        )
        context.documentsAndWebpages.loaded = true

        if (hasNewlyCompletedDocument) {
            context.documentsAndWebpages.completedProcessingRevision = (
                Number(context.documentsAndWebpages.completedProcessingRevision) || 0
            ) + 1
            await publishEffect({
                type: 'request.reply-document-processing.completed',
                phase: 'committed',
                scopes: ['request.reply-documents'],
            })
            await refreshVerificationDocuments()
        }

        return documents
    }

    const applyLinks = response => {
        const items = response && response.data && Array.isArray(response.data.items)
            ? response.data.items
            : []

        context.documentsAndWebpages.links.splice(
            0,
            context.documentsAndWebpages.links.length,
            ...items,
        )
        context.documentsAndWebpages.linksLoaded = true

        return items
    }

    const applyVerificationDocuments = response => {
        const documents = response && Array.isArray(response.data) ? response.data : []

        context.replyDocuments.verificationDocuments.splice(
            0,
            context.replyDocuments.verificationDocuments.length,
            ...documents,
        )
        context.replyDocuments.loaded = true

        return documents
    }

    const chunksOf = (items, size) => {
        const chunks = []
        for (let index = 0; index < items.length; index += size) {
            chunks.push(items.slice(index, index + size))
        }
        return chunks
    }

    const applyResearchProcesses = processes => {
        context.documentsAndWebpages.researchProcesses.splice(
            0,
            context.documentsAndWebpages.researchProcesses.length,
            ...processes,
        )
        context.documentsAndWebpages.researchLoaded = true

        processes.forEach(process => {
            const processId = String(process.processId)
            if (
                !isActionRequiredDocumentResearchStatus(process.status)
                || isTerminalDocumentResearchStatus(process.status)
                || process.status === DocumentResearchStatus.UNAVAILABLE
            ) {
                forcedResearchPollProcessIds.delete(processId)
            }
        })

        return processes
    }

    function refreshResearch() {
        if (refreshResearchPromise) {
            return refreshResearchPromise
        }

        const processIds = researchProcessIds()
        if (processIds.length === 0) {
            return Promise.resolve(applyResearchProcesses([]))
        }

        const getStatuses = getService(context, 'getDocumentResearchStatuses')
        const getProcess = getService(context, 'getDocumentResearchProcess')
        const previousById = new Map(context.documentsAndWebpages.researchProcesses
            .map(process => [String(process.processId), process]))
        const processIdsRequiringDetails = new Set()

        refreshResearchPromise = Promise.all(chunksOf(processIds, RESEARCH_STATUS_BATCH_SIZE)
            .map(ids => getStatuses(ids)))
            .then(responses => responses.reduce((items, response) => (
                items.concat(response && response.data && Array.isArray(response.data.items)
                    ? response.data.items
                    : [])
            ), []))
            .then(items => {
                const statusesById = new Map(items.map(item => [String(item.processId), item]))
                const summaries = processIds.map(processId => {
                    const previous = previousById.get(processId) || {}
                    const summary = statusesById.get(processId)

                    if (
                        summary
                        && (
                            Number(summary.candidateCount) > 0
                            || isActionRequiredDocumentResearchStatus(summary.status)
                        )
                        && (
                            !previous.details
                            || previous.status !== summary.status
                            || previous.candidateCount !== summary.candidateCount
                        )
                    ) {
                        processIdsRequiringDetails.add(processId)
                    }

                    return summary
                        ? { ...previous, ...summary, processId, error: null }
                        : {
                            ...previous,
                            processId,
                            status: DocumentResearchStatus.UNAVAILABLE,
                            error: null,
                        }
                })

                return Promise.all(summaries.map(process => {
                    if (
                        process.status === DocumentResearchStatus.UNAVAILABLE
                        || !processIdsRequiringDetails.has(String(process.processId))
                    ) {
                        return process
                    }

                    return getProcess(process.processId).then(response => ({
                        ...process,
                        details: response && response.data ? response.data : null,
                    }))
                }))
            })
            .then(async processes => {
                const hasNewlyCompletedProcess = processes.some(process => {
                    const previous = previousById.get(String(process.processId))

                    return previous
                        && previous.status !== DocumentResearchStatus.COMPLETED
                        && process.status === DocumentResearchStatus.COMPLETED
                })

                if (hasNewlyCompletedProcess) {
                    await Promise.all([
                        refreshDocuments(),
                        refreshVerificationDocuments(),
                    ])
                }

                reportError(null)
                return applyResearchProcesses(processes)
            })
            .catch(error => {
                reportError(error)
                throw error
            })
            .finally(() => {
                refreshResearchPromise = null
            })

        return refreshResearchPromise
    }

    function refreshDocuments() {
        if (refreshPromise) {
            return refreshPromise
        }

        const getDocuments = getService(context, 'getReceivedRequestUnclassifiedVerificationDocuments')

        refreshPromise = Promise.resolve()
            .then(() => getDocuments())
            .then(async response => {
                reportError(null)
                return applyDocuments(response)
            })
            .catch(error => {
                reportError(error)
                throw error
            })
            .finally(() => {
                refreshPromise = null
            })

        return refreshPromise
    }

    function refreshVerificationDocuments() {
        if (!hasRequestedVerificationDocuments(context)) {
            return Promise.resolve([])
        }

        if (verificationRefreshPromise) {
            return verificationRefreshPromise
        }

        const getDocuments = getService(context, 'getReceivedRequestVerificationDocuments')

        verificationRefreshPromise = Promise.resolve()
            .then(() => getDocuments())
            .then(response => {
                reportError(null)
                return applyVerificationDocuments(response)
            })
            .catch(error => {
                reportError(error)
                throw error
            })
            .finally(() => {
                verificationRefreshPromise = null
            })

        return verificationRefreshPromise
    }

    const refreshLinks = () => {
        if (refreshLinksPromise) {
            return refreshLinksPromise
        }

        const getLinks = getService(context, 'getReceivedRequestDocumentationLinks')

        refreshLinksPromise = Promise.resolve()
            .then(() => getLinks())
            .then(response => {
                reportError(null)
                return applyLinks(response)
            })
            .catch(error => {
                reportError(error)
                throw error
            })
            .finally(() => {
                refreshLinksPromise = null
            })

        return refreshLinksPromise
    }

    const upload = ({ files, revision }) => {
        if (uploadPromise) {
            return uploadPromise
        }

        const uploadDocuments = getService(context, 'uploadReceivedRequestUnclassifiedVerificationDocuments')

        setSaving(true)
        reportError(null)
        uploadPromise = Promise.resolve()
            .then(() => uploadDocuments(files))
            .then(async result => {
                context.documentsAndWebpages.completedUploadRevision = revision
                await publishEffect({
                    type: 'request.reply-documents.changed',
                    phase: 'committed',
                    scopes: ['request.reply-documents'],
                })
                await refreshDocuments()
                schedulePolling()

                return result
            })
            .catch(error => {
                reportError(error)
                throw error
            })
            .finally(() => {
                uploadPromise = null
                setSaving(false)
            })

        return uploadPromise
    }

    const addLinks = ({ urls, revision, failureCallback }) => {
        if (linkSavePromise) {
            return linkSavePromise
        }

        const saveLinks = getService(context, 'addReceivedRequestDocumentationLinks')

        setSaving(true)
        reportError(null)
        linkSavePromise = Promise.resolve()
            .then(() => saveLinks(urls))
            .then(async response => {
                applyLinks(response)
                context.documentsAndWebpages.completedLinkRevision = revision
                await publishEffect({
                    type: 'request.reply-documentation-links.changed',
                    phase: 'committed',
                    scopes: ['request.reply-documentation-links'],
                })
                await refreshResearch()
                scheduleResearchPolling()

                return response
            })
            .catch(error => {
                if (typeof failureCallback === 'function') {
                    failureCallback(error)
                }
                reportError(error)
                throw error
            })
            .finally(() => {
                linkSavePromise = null
                setSaving(false)
            })

        return linkSavePromise
    }

    const runResearchCommand = ({ serviceName, servicePayload, processId, successCallback, failureCallback }) => {
        if (researchCommandPromise) {
            return researchCommandPromise
        }

        const command = getService(context, serviceName)
        setSaving(true)
        reportError(null)
        researchCommandPromise = Promise.resolve()
            .then(() => command(servicePayload))
            .then(async response => {
                forcedResearchPollProcessIds.add(String(processId))
                if (typeof successCallback === 'function') {
                    successCallback(response)
                }
                await refreshResearch()
                scheduleResearchPolling()
                return response
            })
            .catch(async error => {
                if (typeof failureCallback === 'function') {
                    failureCallback(error)
                }
                reportError(error)
                if (error && error.response && error.response.status === 409) {
                    await refreshResearch().catch(() => undefined)
                }
                throw error
            })
            .finally(() => {
                researchCommandPromise = null
                setSaving(false)
            })

        return researchCommandPromise
    }

    const previewResearchCandidate = ({ processId, candidateId, successCallback, failureCallback }) => {
        const getContent = getService(context, 'getDocumentResearchCandidateContent')

        return Promise.resolve()
            .then(() => getContent({ processId, candidateId }))
            .then(response => {
                if (typeof successCallback === 'function') {
                    successCallback(response)
                }
                return response
            })
            .catch(error => {
                if (typeof failureCallback === 'function') {
                    failureCallback(error)
                }
                return null
            })
    }

    const syncReviewRefreshState = () => {
        context.documentsAndWebpages.reviewRefreshPendingProcessIds.splice(
            0,
            context.documentsAndWebpages.reviewRefreshPendingProcessIds.length,
            ...reviewRefreshAttempts.keys(),
        )
    }

    const finishReviewRefreshes = documents => {
        reviewRefreshAttempts.forEach((attempts, processId) => {
            const document = documents.find(item => (
                item
                && item.processing
                && String(item.processing.processId) === processId
            ))
            const isStillPending = document
                && getDocumentProcessingStatus(document) === DocumentProcessingStatus.REVIEW_PENDING

            if (!isStillPending || attempts >= reviewRefreshMaxAttempts) {
                reviewRefreshAttempts.delete(processId)
            }
        })
        syncReviewRefreshState()
    }

    const finishFailedReviewRefresh = () => {
        reviewRefreshAttempts.forEach((attempts, processId) => {
            if (attempts >= reviewRefreshMaxAttempts) {
                reviewRefreshAttempts.delete(processId)
            }
        })
        syncReviewRefreshState()
    }

    const scheduleReviewRefresh = delay => {
        if (reviewRefreshTimeout || disposed || reviewRefreshAttempts.size === 0) {
            return
        }

        reviewRefreshTimeout = setTimeout(() => {
            reviewRefreshTimeout = null
            reviewRefreshAttempts.forEach((attempts, processId) => {
                reviewRefreshAttempts.set(processId, attempts + 1)
            })
            refreshDocuments()
                .then(finishReviewRefreshes)
                .catch(finishFailedReviewRefresh)
                .finally(() => {
                    if (reviewRefreshAttempts.size > 0) {
                        scheduleReviewRefresh(reviewRefreshInterval)
                    }
                    schedulePolling()
                })
        }, delay)
    }

    const startReviewRefresh = processId => {
        if (processId === null || typeof processId === 'undefined' || processId === '') {
            return
        }

        reviewRefreshAttempts.set(String(processId), 0)
        syncReviewRefreshState()
        scheduleReviewRefresh(reviewRefreshDelay)
    }

    return Object.freeze({
        async load() {
            await Promise.all([
                refreshDocuments(),
                refreshVerificationDocuments(),
                linksEnabled ? refreshLinks() : Promise.resolve([]),
            ])
            await (linksEnabled ? refreshResearch() : Promise.resolve([]))
        },
        activate() {
            active = true
            schedulePolling()
            scheduleResearchPolling()
        },
        deactivate() {
            active = false
            stopPolling()
            stopResearchPolling()
        },
        setValidation(nextValidation) {
            validation = nextValidation
        },
        validate() {
            return Promise.resolve(validation)
        },
        handleAction(action) {
            if (action.type === 'documents-and-webpages.upload') {
                return upload(action.payload)
            }

            if (action.type === 'documents-and-webpages.links.add') {
                return addLinks(action.payload)
            }

            if (action.type === 'documents-and-webpages.review-completed') {
                startReviewRefresh(action.payload && action.payload.processId)
                return Promise.resolve()
            }

            if (action.type === 'documents-and-webpages.research.preview') {
                return previewResearchCandidate(action.payload)
            }

            if (action.type === 'documents-and-webpages.research.select') {
                return runResearchCommand({
                    ...action.payload,
                    serviceName: 'selectDocumentResearchDocuments',
                    servicePayload: {
                        processId: action.payload.processId,
                        candidateIds: action.payload.candidateIds,
                    },
                })
            }

            if (action.type === 'documents-and-webpages.research.finish-without-documents') {
                return runResearchCommand({
                    ...action.payload,
                    serviceName: 'finishDocumentResearchWithoutDocuments',
                    servicePayload: action.payload.processId,
                })
            }

            if (action.type === 'documents-and-webpages.research.review-completed') {
                forcedResearchPollProcessIds.add(String(action.payload.processId))
                scheduleResearchPolling()
                return refreshResearch()
            }

            throw new Error(`Unsupported Documents and Webpages action "${action.type}"`)
        },
        async flush() {
            await (uploadPromise || Promise.resolve())
            await (linkSavePromise || Promise.resolve())
            await (refreshPromise || Promise.resolve())
            await (verificationRefreshPromise || Promise.resolve())
            await (refreshLinksPromise || Promise.resolve())
            await (researchCommandPromise || Promise.resolve())
            await (refreshResearchPromise || Promise.resolve())
        },
        dispose() {
            disposed = true
            active = false
            stopPolling()
            stopResearchPolling()
            clearTimeout(reviewRefreshTimeout)
            reviewRefreshTimeout = null
            reviewRefreshAttempts.clear()
            forcedResearchPollProcessIds.clear()
            syncReviewRefreshState()
        },
    })
}

export { createDocumentsAndWebpagesController }
export default createDocumentsAndWebpagesController
